const React = require('react');
const { useEffect, useState, useRef } = React;
const { default: Styled } = require('styled-components');
const { default: TextField } = require('@mui/material/TextField');
const { default: FormControlLabel } = require('@mui/material/FormControlLabel');
const { default: Checkbox } = require('@mui/material/Checkbox');
const { default: Button } = require('@mui/material/Button');
const { context } = require('app-context');
const { useAppSelector } = require('utils/redux');

const { getDisplayImageUrl } = require('utils/image');

const Ui = require('components/Gamification/styles');

const internals = {};

module.exports = function GamificationAdminPage() {

    const schoolId = useAppSelector(context.selectors.all.getCurrentSchoolId);
    const user = useAppSelector(context.selectors.all.getCurrentUserObject);
    const { Editor } = internals;
    return <Editor key={`${schoolId}:${user?.id}`} />;
};

internals.blankPrize = (weekStart) => ({ weekStart, description: '', imageUrl: null, sponsor: '', rankCutoff: 10, redemptionInstructions: '', enabled: false });
internals.editablePrize = (prize) => Object.fromEntries(Object.keys(internals.blankPrize(prize.weekStart)).map((key) => [key, prize[key]]));

internals.Editor = function Editor() {

    const [data, setData] = useState(null);
    const [prize, setPrize] = useState(null);
    const [contacts, setContacts] = useState('');
    const [winners, setWinners] = useState(null);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const live = useRef(true);
    const { Page, Panel, Fields, Actions, TableWrapper } = internals;
    const api = context.api.nearpeer.gamification;
    useEffect(() => {

        live.current = true;
        api.admin().then((result) => {

            if (!live.current) { return; }
            setData(result);
            setContacts(result.recipientEmails.join('\n'));
            setPrize(internals.editablePrize(result.prizes.find((p) => p.weekStart === result.currentWeek) || internals.blankPrize(result.currentWeek)));
        }).catch((error) => live.current && setMessage(error?.response?.data?.message || 'Gamification administration is unavailable.'));
        return () => { live.current = false; };
    }, []);

    const run = async (action, success) => {

        setBusy(true);
        setMessage('');
        try {
            const result = await action();
            if (live.current) { if (success) { success(result); } }
        }
        catch (error) { if (live.current) { setMessage(error?.response?.data?.message || 'Unable to save. Please try again.'); } }
        finally { if (live.current) { setBusy(false); } }
    };
    const change = (key, value) => setPrize((old) => ({ ...old, [key]: value }));
    if (!data || !prize) { return <Page><p role='status'>{message || 'Loading gamification administration…'}</p></Page>; }
    const locked = prize.weekStart < data.currentWeek || data.prizes.some((p) => p.weekStart === prize.weekStart && p.awardedAt);
    return <Page>
        <h1>Manage Gamification</h1>
        <p>Students · {data.population} active accounts · Weekly honors: Top {data.honorCutoff}</p>
        <p>Weeks run Monday–Sunday in {data.timeZone}. Honors use the one-third policy, with Top 10 as the minimum for growing communities.</p>
        {!!message && <p role='status'>{message}</p>}
        <Panel>
            <h2>Weekly prize</h2>
            <p>One prize for the Top X students. Winners receive a bell notification with their unique claim code.</p>
            <p>Edit this week’s prize to update the homepage, or choose a future Monday to schedule a prize. Only published prizes appear on the homepage during their selected week.</p>
            <form onSubmit={(event) => {

                event.preventDefault();
                run(() => api.savePrize(prize), (saved) => {

                    setData((old) => ({ ...old, prizes: [{ ...saved }, ...old.prizes.filter((p) => p.weekStart !== saved.weekStart)] }));
                    setMessage(!saved.enabled ? 'Prize saved as unpublished. It will not appear on the homepage.' : saved.weekStart === data.currentWeek ? 'Prize saved. The homepage will show these details when you return to it.' : `Prize saved. It will appear on the homepage for the week starting ${saved.weekStart}.`);
                });
            }}>
                <Fields disabled={busy}>
                    <TextField fullWidth variant='filled' label='Week starting (Monday)' type='date' InputLabelProps={{ shrink: true }} value={prize.weekStart} required disabled={busy} onChange={(event) => {

                        const date = event.target.value;
                        setPrize(internals.editablePrize(data.prizes.find((p) => p.weekStart === date) || internals.blankPrize(date)));
                    }} />
                    <fieldset disabled={locked}>
                        <TextField fullWidth variant='filled' label='Prize description' multiline minRows={3} required inputProps={{ maxLength: 4000 }} disabled={busy || locked} value={prize.description} onChange={(e) => change('description', e.target.value)} />
                        <TextField fullWidth variant='filled' label='Sponsor' inputProps={{ maxLength: 255 }} disabled={busy || locked} value={prize.sponsor || ''} onChange={(e) => change('sponsor', e.target.value)} />
                        <TextField fullWidth variant='filled' label='Prize goes to the Top' type='number' required inputProps={{ min: 1, max: 100 }} disabled={busy || locked} value={prize.rankCutoff} onChange={(e) => change('rankCutoff', Number(e.target.value))} />
                        <TextField fullWidth variant='filled' label='Redemption instructions' multiline minRows={3} required inputProps={{ maxLength: 4000 }} disabled={busy || locked} value={prize.redemptionInstructions} onChange={(e) => change('redemptionInstructions', e.target.value)} />
                        <label className='file-label'>Prize image<input type='file' accept='image/jpeg,image/png,image/webp' onChange={(event) => {

                            const file = event.target.files?.[0];
                            if (!file) { return; }
                            if (file.size > 10 * 1024 * 1024) { setMessage('Choose an image smaller than 10 MB.'); return; }
                            run(() => api.uploadPrizeImage({ prizeImage: file }), (result) => { change('imageUrl', result.imageUrl); setMessage('Image uploaded. Save the prize to use it.'); });
                        }} /></label>
                        {!!prize.imageUrl && <><img src={getDisplayImageUrl(prize.imageUrl)} alt='Prize preview' /><Button type='button' onClick={() => change('imageUrl', null)}>Remove image</Button></>}
                        <FormControlLabel label='Publish this prize' control={<Checkbox checked={prize.enabled} disabled={busy || locked} onChange={(e) => change('enabled', e.target.checked)} />} />
                        <Actions><Button variant='contained' type='submit'>Save prize</Button></Actions>
                    </fieldset>
                </Fields>
            </form>
            {locked && <p>Completed-week prizes are read-only.</p>}
        </Panel>
        <Panel>
            <h2>Winner-list recipients</h2>
            <p>At week’s end, each address receives a private email with winners’ names, email addresses, and unique claim codes. Print the checklist for the register and cross off redeemed prizes. Nearpeer does not track redemption.</p>
            <form onSubmit={(event) => {

                event.preventDefault();
                run(() => api.savePrizeContacts(contacts.split(/[\n,;]+/).map((email) => email.trim()).filter(Boolean)), (result) => { setContacts(result.recipientEmails.join('\n')); setData((old) => ({ ...old, recipientEmails: result.recipientEmails })); setMessage('Recipient list saved.'); });
            }}>
                <Fields disabled={busy}><TextField fullWidth variant='filled' label='Email addresses (one per line)' multiline minRows={4} disabled={busy} value={contacts} onChange={(e) => setContacts(e.target.value)} /><Button variant='contained' type='submit'>Save recipients</Button></Fields>
            </form>
            {!data.recipientEmails.length && <p>No recipients configured. Winner-list emails wait until a recipient is added.</p>}
        </Panel>
        <Panel>
            <h2>Completed weeks</h2>
            {!data.prizes.some((p) => p.awardedAt) && <p>Winners will appear here after the first prize week ends.</p>}
            {data.prizes.filter((p) => p.awardedAt).map((p) => <p key={p.id}><Button disabled={busy} onClick={() => run(() => api.prizeWinners(p.id), setWinners)}>View winners: {p.weekStart}</Button> · {p.winnerEmailsQueuedAt ? 'Winner emails queued' : 'Winner emails pending'}</p>)}
            {winners && <>
                <h3>Winners for {winners.prize.weekStart}</h3>
                <Button onClick={() => internals.downloadWinners(winners)}>Download winner checklist</Button>
                <TableWrapper><table><thead><tr><th>Rank</th><th>Name</th><th>Email</th><th>Claim code</th></tr></thead><tbody>{winners.winners.map((w) => <tr key={w.claimCode}><td>{w.rank}</td><td>{w.firstName} {w.lastName}</td><td>{w.email}</td><td>{w.claimCode}</td></tr>)}</tbody></table></TableWrapper>
                {!winners.winners.length && <p>No qualifying winners this week.</p>}
            </>}
        </Panel>
    </Page>;
};

internals.downloadWinners = ({ prize, winners }) => {

    const cell = (value) => `"${String(value ?? '').replace(/^[=+@-]/, "'$&").replace(/"/g, '""')}"`;
    const rows = [['Redeemed', 'Rank', 'First name', 'Last name', 'Email', 'Claim code'], ...winners.map((w) => ['', w.rank, w.firstName, w.lastName, w.email, w.claimCode])];
    const url = URL.createObjectURL(new Blob([rows.map((row) => row.map(cell).join(',')).join('\r\n')], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `nearpeer-winners-${prize.weekStart}.csv`;
    link.click();
    URL.revokeObjectURL(url);
};

internals.Page = Styled.div`${Ui.body}box-sizing: border-box; width: 100%; max-width: 850px; margin: 24px auto; padding: 0 16px;  h1 { font-size: 26px; } p, label { font-size: 16px; line-height: 1.5; }`;
internals.Panel = Styled.section`${Ui.surface}margin: 20px 0; padding: 20px;    h2 { font-size: 20px; margin-top: 0; } img { display: block; max-width: 100%; max-height: 200px; margin: 12px 0; }`;
internals.Fields = Styled.fieldset`border: 0; padding: 0; margin: 0; min-width: 0; fieldset { border: 0; padding: 0; min-width: 0; } > .MuiFormControl-root, fieldset > .MuiFormControl-root { margin-bottom: 16px; } .file-label { display: flex; flex-direction: column; gap: 6px; margin-bottom: 16px; } input[type=file] { font: inherit; max-width: 100%; }`;
internals.Actions = Styled.div`display: flex; justify-content: center; margin-top: 16px;`;
internals.TableWrapper = Styled.div`overflow-x: auto; table { border-collapse: collapse; width: 100%; font-size: 14px; } th, td { text-align: left; border: 1px solid ${Ui.divider}; padding: 8px; }`;
